import type { LevelCatalog, PassClaim, ProductBuyOutcome, Season } from "@highmarch/app";
import { seasonView } from "@highmarch/app";
import type { Rarity } from "@highmarch/domain";
import { sheetHeadHtml, tickTimers } from "../components";
import { toast } from "../toast";
import { uiUrl } from "../portraits";
import { paidContents, rewardChest } from "../chestReveal";
import { paidLine } from "../hub/eventRewards";
import { flyReward } from "../hub/ordersSheet";
import { confirmPurchase, econCue, purchasePop } from "../hub/purchaseFx";
import { blockedText, fmtCountdown, purchaseFailText } from "../deals/dealsModel";
import { showItemFocus } from "../deals/pickSheet";
import { SHOP_SKIN } from "../shopSkin";
import { PASS_ICON, PassCtx, footHtml, heroBlockHtml, offersHtml, summaryHtml, tierPlateHtml, trackHtml } from "./passMarkup";
import { RARITY_LABEL } from "../gearUi";
import { SKIN_LOOKS } from "../../render/skins/looks";
import "../styles/season.css";

/**
 * The Pass sheet, "War Campaign" (MP-22): a parchment hub sheet in the shop skin like Deals — the header (the campaign
 * banner, the gems pill), Captain Bram's hero with the season's countdown, the tier plate, the two-row track, the
 * Premium and Premium+ cards, the rollover summary when a season just ended. Opened only by a tap in the hub (the
 * Pass button, Deals' War Campaign tab): never from a level or the end screen. Claims are free and pay at once.
 */

const TICK_MS = 1000;
/** How long a bought card's glow plays before the sheet redraws. */
const REVEAL_MS = 700;

export interface PassDeps {
  readonly catalog: LevelCatalog;
  readonly season: Season;
  readonly parent: HTMLElement;
  now(): number;
  /** Premium can be bought here (a store is open: StoreKit, or the web's sandbox with ?debug/?sandboxstore). */
  paid(): boolean;
  readonly storeKind: "sandbox" | "storekit2" | null;
  prices(): Promise<ReadonlyMap<string, string>>;
  buy(productId: string): Promise<ProductBuyOutcome>;
  /** Save and redraw the hub (its pills, the Pass button's dot). */
  changed(): void;
  /** Draw inside this element (the Store's Pass page, owner 2026-10-21) instead of a sheet of its own. */
  readonly embed?: HTMLElement;
}

export function openPass(d: PassDeps): HTMLElement {
  return new PassScreen(d).el;
}

class PassScreen {
  readonly el: HTMLDivElement;
  private prices: ReadonlyMap<string, string> = new Map();
  private timer = 0;
  private busy = false;
  private firstDraw = true;

  constructor(private readonly d: PassDeps) {
    this.el = document.createElement("div");
    this.el.className = d.embed ? "pembed" : `sheet panel pass ${SHOP_SKIN}`;
    this.render();
    (d.embed ?? d.parent).append(this.el);
    this.focusTrack();
    this.timer = window.setInterval(() => this.tick(), TICK_MS);
    void d.prices().then((p) => { this.prices = p; if (this.el.isConnected) this.render(); }).catch(() => undefined);
  }

  private ctx(now: number): PassCtx {
    return { balance: this.d.catalog.balance, level: Math.max(1, this.d.catalog.highestLevelCleared), now, prices: this.prices, paid: this.d.paid(), sandbox: this.d.storeKind === "sandbox", ownsSkin: this.d.catalog.skinGrants.ownsSkin };
  }

  private render(): void {
    const now = this.d.now(), v = seasonView(this.d.catalog, this.d.season, now), c = this.ctx(now), data = this.d.catalog.balance.seasons;
    const scroller = this.d.embed ? null : this.el.querySelector<HTMLElement>(".dscroll"), track = this.el.querySelector<HTMLElement>(".pscroll");
    const keep = scroller?.scrollTop ?? 0, keepX = track?.scrollLeft ?? 0;
    // The head: the campaign banner, the title, the season (its name is the hero's); no currency pill: nothing here costs gems.
    const page = `<section class="dpage ppage">${v.summary ? summaryHtml(v.summary, c, data, v.theme.name) : ""}${heroBlockHtml(v, c)}
      ${v.live ? tierPlateHtml(v) : ""}${trackHtml(v, c)}${offersHtml(v, c, data)}${footHtml(c, data)}</section>`;
    this.el.innerHTML = this.d.embed ? page
      : sheetHeadHtml({ title: "War Campaign", sub: `Season ${v.number} · ${v.tiers} tiers`, portrait: uiUrl(PASS_ICON), tint: "gold" }) + `<div class="dscroll">${page}</div>`;
    const ns = this.el.querySelector<HTMLElement>(".dscroll"), nt = this.el.querySelector<HTMLElement>(".pscroll");
    if (ns) ns.scrollTop = keep;
    if (nt && !this.firstDraw) nt.scrollLeft = keepX;
    this.firstDraw = false;
    this.bind();
  }

  /** The track opens on the first reward to claim, else the tier being filled. */
  private focusTrack(): void {
    const track = this.el.querySelector<HTMLElement>(".pscroll");
    const col = this.el.querySelector<HTMLElement>(".pcol:has(.s-ready)") ?? this.el.querySelector<HTMLElement>(".pcol.cur") ?? null;
    if (track && col) track.scrollLeft = Math.max(0, col.offsetLeft - track.clientWidth / 2 + col.clientWidth / 2);
  }

  private tick(): void {
    if (!this.el.isConnected) { window.clearInterval(this.timer); return; }
    if (tickTimers(this.el, this.d.now(), fmtCountdown) && !this.busy) this.render();
  }

  private bind(): void {
    const q = <T extends HTMLElement>(s: string) => [...this.el.querySelectorAll<T>(s)];
    const close = this.el.querySelector<HTMLButtonElement>(".k-head .close");
    if (close) close.onclick = () => this.close();
    for (const b of q<HTMLButtonElement>(".pcell[data-tier]")) b.onclick = () => this.tapCell(b);
    this.el.querySelector<HTMLButtonElement>(".pall")?.addEventListener("click", () => this.claimAll());
    for (const b of q<HTMLButtonElement>(".pbuy[data-product]")) b.onclick = () => void this.buy(b.dataset.product!, b);
    this.el.querySelector<HTMLButtonElement>(".pseen")?.addEventListener("click", () => { this.d.season.dismissSummary(); this.d.changed(); this.render(); });
  }

  close(): void { window.clearInterval(this.timer); this.el.remove(); }

  private tapCell(b: HTMLButtonElement): void {
    const track = b.dataset.track === "premium" ? "premium" : "free", tier = Number(b.dataset.tier);
    if (!b.classList.contains("s-ready") && b.dataset.chest !== undefined) {
      // a chest not yet to claim: it comes into focus with its odds (owner 2026-10-21: no ODDS button)
      void showItemFocus(this.d.parent, this.d.catalog.balance, Math.max(1, this.d.catalog.highestLevelCleared), { kind: "chest", rarity: Number(b.dataset.chest) as Rarity, count: 1 });
      return;
    }
    if (!b.classList.contains("s-ready")) {
      const why = b.classList.contains("s-claimed") ? "Claimed" : b.classList.contains("s-locked") ? "Premium reward" : `Tier ${tier}`;
      toast(this.d.parent, `${why}: ${b.dataset.words ?? ""}`);
      return;
    }
    const r = this.d.season.claim(track, tier, this.d.now());
    if (typeof r === "string") { toast(this.d.parent, r === "not ready" ? "Not reached yet" : blockedText(r)); return; }
    econCue("event");
    this.d.changed();
    flyReward(b, r.paid.gold);
    this.show([r]);
  }

  private claimAll(): void {
    const r = this.d.season.claimAll(this.d.now());
    if (typeof r === "string") { toast(this.d.parent, blockedText(r)); return; }
    if (r.length === 0) return;
    econCue("event");
    this.d.changed();
    this.show(r);
  }

  /** What claims paid: each chest opens on the chest stage in turn, then one line says the rest. */
  private show(claims: readonly PassClaim[]): void {
    const lines = claims.map((c) => paidLine(c.paid, c.gear)).filter(Boolean);
    const titles = claims.map((c) => c.title).filter(Boolean);
    const skins = claims.map((c) => SKIN_LOOKS[c.skin]?.name ?? "").filter(Boolean);
    // a skin already had (a later loop of the season list) paid its gems instead: say which
    const owned = claims.filter((c) => c.skinGems > 0).map((c) => `${SKIN_LOOKS[c.ownedSkin]?.name ?? "Skin"} already yours: +${c.skinGems.toLocaleString("en-US")} gems`);
    const line = [claims.length > 1 ? `${claims.length} rewards` : "", lines.length <= 2 ? lines.join(" · ") : "", ...skins.map((k) => `New skin: ${k}`), ...owned, ...titles.map((t) => `New title: ${t}`)].filter(Boolean).join(" · ");
    const chests = claims.filter((c) => c.paid.chest !== null);
    let k = 0;
    const next = (): void => {
      if (k >= chests.length) { if (line) toast(this.d.parent, line, 2400); if (this.el.isConnected) this.render(); return; }
      const c = chests[k++];
      rewardChest(this.d.parent, this.d.catalog.balance, c.paid.chest!.rarity, paidContents(c.paid, c.gear), `Tier ${c.tier} · ${RARITY_LABEL[c.paid.chest!.rarity]} chest`, next);
    };
    next();
  }

  private async buy(productId: string, btn: HTMLButtonElement): Promise<void> {
    if (this.busy) return;
    const v = seasonView(this.d.catalog, this.d.season, this.d.now());
    const p = [v.premium, v.plus].find((x) => x.product === productId);
    if (!p) return;
    if (p.blocked) { toast(this.d.parent, blockedText(p.blocked)); return; }
    const price = this.prices.get(productId) ?? p.priceLabel;
    if (this.d.storeKind === "sandbox") {
      const ok = await confirmPurchase(this.d.parent, { shop: true, title: "TEST PURCHASE", currency: null, price, confirm: "BUY", className: "dconfirm",
        body: `<p class="dcname"><b>${p.name}</b></p><p>Season ${v.number}: ${v.theme.name}${p.tierSkip > 0 ? `, +${p.tierSkip} tiers at once` : ""}.</p><p>Test build: nothing is charged.</p>` });
      if (!ok) return;
    }
    this.busy = true;
    btn.disabled = true;
    btn.classList.add("busy");
    const card = btn.closest(".poffer");
    try {
      const r = await this.d.buy(productId);
      if (r.status === "granted") {
        econCue("buy");
        this.d.changed();
        purchasePop(card);
        toast(this.d.parent, `${p.level === "plus" ? "Premium+" : "Premium"} is yours. Thank you, Commander!`, 2400);
        window.setTimeout(() => { if (this.el.isConnected) { this.render(); this.focusTrack(); } }, REVEAL_MS);
      } else if (r.status === "pending") toast(this.d.parent, "Awaiting approval: Premium arrives the moment it's given");
      else if (r.status === "refused" || r.status === "failed") toast(this.d.parent, purchaseFailText(r.reason));
    } finally {
      this.busy = false;
      if (this.el.isConnected) this.render();
    }
  }
}
